import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  inject, model, OnDestroy, OnInit, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { KartWebSocketService, BagliCihaz } from '../kart-okuyucu/services/kart-websocket.service';

@Component({
  selector: 'firmada-panel-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './firmada-panel-edit.component.html',
  styleUrl: './firmada-panel-edit.component.less',
})
export class FirmadaPanelEditComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  parametreler = model<any>();

  cihazlar        = signal<BagliCihaz[]>([]);
  secilenler      = signal<string[]>([]);   // cihaz_kimlik dizisi
  kartDurumMap    = new Map<string, boolean>();

  private _destroy$ = new Subject<void>();
  private wsService = inject(KartWebSocketService);
  private cd        = inject(ChangeDetectorRef);

  constructor() { super(); }

  ngOnInit() {
    const ek = this.parametreler()?.ek ?? {};
    if (Array.isArray(ek.secilenOkuyucular)) {
      this.secilenler.set([...ek.secilenOkuyucular]);
    }

    this.wsService.cihazlar$.pipe(takeUntil(this._destroy$)).subscribe(liste => {
      const filtreli = liste.filter(c =>
        !c.tur || c.tur === 'kart_okuyucu' || c.tur === 'rfid' || c.tur === 'nfc'
      );
      this.cihazlar.set(filtreli.length ? filtreli : liste);
      this.cd.markForCheck();
    });

    this.wsService.kartOlayi$.pipe(takeUntil(this._destroy$)).subscribe(olay => {
      this.kartDurumMap.set(olay.cihazKimlik, olay.durum === 'koyuldu');
      this.cd.markForCheck();
    });

    if (!this.wsService.bagli) this.wsService.baglan();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._destroy$.next();
    this._destroy$.complete();
  }

  seciliMi(kimlik: string): boolean {
    return this.secilenler().includes(kimlik);
  }

  toggle(cihaz: BagliCihaz) {
    const mevcut = this.secilenler();
    const yeni = mevcut.includes(cihaz.cihaz_kimlik)
      ? mevcut.filter(k => k !== cihaz.cihaz_kimlik)
      : [...mevcut, cihaz.cihaz_kimlik];
    this.secilenler.set(yeni);
    this._kaydet(yeni);
  }

  cikar(kimlik: string) {
    const yeni = this.secilenler().filter(k => k !== kimlik);
    this.secilenler.set(yeni);
    this._kaydet(yeni);
  }

  cihazIsmi(kimlik: string): string {
    return this.cihazlar().find(c => c.cihaz_kimlik === kimlik)?.cihaz_ismi || kimlik;
  }

  cihazDurumu(cihaz: BagliCihaz): string {
    const v = this.kartDurumMap.get(cihaz.cihaz_kimlik);
    if (v === true)  return '💳 Kart okutuldu';
    if (v === false) return '⏳ Kart bekleniyor';
    return '📡 Bağlı';
  }

  baslikGuncelle(v: string) {
    this._ekGuncelle('baslik', v);
  }

  private _kaydet(liste: string[]) {
    this._ekGuncelle('secilenOkuyucular', liste);
  }

  /**
   * Gerçek alanları parametreler().ek içine yazar. `ek`, kart içeriğinin
   * (icerik.ek) doğrudan referansı olduğu için burada mutasyon yapmak,
   * kartın kaydedilen verisine de aynen yansır (bkz. kart-sahibi-edit.component.ts
   * ekGuncelle — { ...(p.ek ?? {}) } ile yeni obje kurmak bu referansı kopardığı
   * için değişiklikler ekranda görünse de DB'ye hiç yazılmıyordu).
   */
  private _ekGuncelle(alan: string, deger: any) {
    const params = this.parametreler() ?? {};
    const ek = params.ek ?? {};
    ek[alan] = deger;
    this.parametreler.set({ ...params, ek });
    this.cd.markForCheck();
  }
}
